import React, { useState } from "react";
import config from "../data/siteConfig";
import { Ornament, MandalaCorner, BlockPrintBg } from "./Decor";
import { FaWhatsapp } from "react-icons/fa";
import { FiCalendar, FiShoppingBag, FiMessageSquare } from "react-icons/fi";

// Showroom hours are 10:00 AM – 9:00 PM, so offer half-hour slots in that range.
const TIME_SLOTS = (() => {
  const slots = [];
  for (let m = 10 * 60; m <= 21 * 60; m += 30) {
    const h24 = Math.floor(m / 60);
    const mins = m % 60;
    const suffix = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    slots.push(`${h12}:${String(mins).padStart(2, "0")} ${suffix}`);
  }
  return slots;
})();

const TABS = [
  { id: "appointment", label: "Book an Appointment", Icon: FiCalendar },
  { id: "order", label: "Place an Order", Icon: FiShoppingBag },
  { id: "contact", label: "Contact Us", Icon: FiMessageSquare },
];

const today = () => new Date().toISOString().split("T")[0];

// dd/mm/yyyy reads more naturally than the raw yyyy-mm-dd input value
const prettyDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const field =
  "w-full rounded-md border border-gold/40 bg-cream/70 px-4 py-2.5 text-coffee-800 " +
  "outline-none transition-colors placeholder:text-coffee-500/60 focus:border-gold focus:bg-cream";
const labelCls =
  "mb-1.5 block text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-coffee-600";

export default function GetInTouch() {
  const [tab, setTab] = useState("appointment");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    date: "",
    time: "",
    item: "",
    budget: "",
    callTime: "",
    message: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Build the WhatsApp message for the active tab and hand off to wa.me.
  const submit = (e) => {
    e.preventDefault();
    const brand = config.brand.name;
    let lines;

    if (tab === "appointment") {
      lines = [
        `*Appointment Request* — ${brand}`,
        "",
        `Name: ${form.name}`,
        `Phone: ${form.phone}`,
        `Preferred Date: ${prettyDate(form.date)}`,
        `Preferred Time: ${form.time}`,
      ];
    } else if (tab === "order") {
      lines = [
        `*Order Enquiry* — ${brand}`,
        "",
        `Item Description: ${form.item}`,
        `Budget: ${form.budget}`,
        `Preferred Call Time: ${form.callTime}`,
      ];
    } else {
      lines = [
        `*Enquiry* — ${brand}`,
        "",
        `Name: ${form.name}`,
        `Phone: ${form.phone}`,
        `Message: ${form.message}`,
      ];
    }

    const url = `https://wa.me/${config.contact.whatsapp}?text=${encodeURIComponent(
      lines.join("\n")
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const activeTab = TABS.find((t) => t.id === tab);

  return (
    <section id="enquiry" className="relative overflow-hidden py-20 md:py-28">
      <BlockPrintBg className="absolute inset-0 text-coffee-700" opacity={0.05} />
      <MandalaCorner className="absolute -left-6 top-8 h-40 w-40 text-coffee-300/40" />
      <MandalaCorner className="absolute -right-6 bottom-8 h-40 w-40 -scale-x-100 text-coffee-300/40" />

      <div className="container-x relative">
        <div className="text-center">
          <span className="eyebrow">We're Here to Help</span>
          <h2 className="mt-2 font-display text-3xl text-coffee-800 sm:text-4xl">Get in Touch</h2>
          <div className="mt-3 flex justify-center"><Ornament /></div>
          <p className="mx-auto mt-5 max-w-xl text-coffee-700/90">
            Book a visit, tell us what you'd like made, or simply say hello — your details go
            straight to us on WhatsApp.
          </p>
        </div>

        <div className="mx-auto mt-12 max-w-2xl overflow-hidden rounded-xl border-2 border-gold/60 bg-cream/50 shadow-vintage">
          {/* tabs */}
          <div role="tablist" aria-label="Enquiry type" className="grid grid-cols-3 border-b-2 border-gold/40">
            {TABS.map(({ id, label, Icon }) => {
              const on = tab === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setTab(id)}
                  className={`flex items-center justify-center gap-2 px-3 py-4 text-[0.72rem] font-semibold uppercase tracking-[0.1em] transition-colors sm:text-[0.78rem] ${
                    on
                      ? "bg-coffee-grad text-gold-light"
                      : "bg-transparent text-coffee-600 hover:bg-gold/10 hover:text-coffee-900"
                  }`}
                >
                  <Icon className="shrink-0 text-base" />
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden">{label.split(" ").slice(-1)}</span>
                </button>
              );
            })}
          </div>

          <form onSubmit={submit} className="p-7 md:p-9">
            <h3 className="mb-6 flex items-center gap-2 font-display text-xl text-coffee-800">
              <activeTab.Icon className="text-gold-deep" /> {activeTab.label}
            </h3>

            {tab === "appointment" && (
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="ap-name">Your Name</label>
                  <input id="ap-name" className={field} required value={form.name}
                         onChange={set("name")} placeholder="e.g. Ramesh Patel" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="ap-phone">Phone Number</label>
                  <input id="ap-phone" className={field} required type="tel" value={form.phone}
                         onChange={set("phone")} placeholder="e.g. 98250 12345" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="ap-date">Preferred Date</label>
                  <input id="ap-date" className={field} required type="date" min={today()}
                         value={form.date} onChange={set("date")} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="ap-time">Preferred Time</label>
                  <select id="ap-time" className={field} required value={form.time} onChange={set("time")}>
                    <option value="">Select a time</option>
                    {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            )}

            {tab === "order" && (
              <div className="grid gap-5">
                <div>
                  <label className={labelCls} htmlFor="or-item">Item Description</label>
                  <textarea id="or-item" className={`${field} min-h-[110px] resize-y`} required
                            value={form.item} onChange={set("item")}
                            placeholder="Describe the piece you'd like — e.g. antique bridal choker set, 22K, around 40g" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="or-budget">Budget</label>
                    <input id="or-budget" className={field} required value={form.budget}
                           onChange={set("budget")} placeholder="e.g. ₹1,50,000" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="or-calltime">Preferred Call Time</label>
                    <select id="or-calltime" className={field} required value={form.callTime}
                            onChange={set("callTime")}>
                      <option value="">Select a time</option>
                      {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {tab === "contact" && (
              <div className="grid gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="ct-name">Your Name</label>
                    <input id="ct-name" className={field} required value={form.name}
                           onChange={set("name")} placeholder="e.g. Ramesh Patel" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ct-phone">Phone Number</label>
                    <input id="ct-phone" className={field} required type="tel" value={form.phone}
                           onChange={set("phone")} placeholder="e.g. 98250 12345" />
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="ct-message">Message</label>
                  <textarea id="ct-message" className={`${field} min-h-[110px] resize-y`} required
                            value={form.message} onChange={set("message")}
                            placeholder="How can we help you?" />
                </div>
              </div>
            )}

            <button type="submit" className="btn btn-gold mt-7 w-full justify-center sm:w-auto">
              <FaWhatsapp className="text-lg" /> Send on WhatsApp
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
